import axios from 'axios';
import {
  Payment,
  PaymentSearchParams,
  PaymentListResponse,
  PaymentStats
} from '../models/PaymentModels';
import { setupApiInterceptors } from '../utils/apiInterceptor';
import { getApiBaseUrl } from '../utils/apiBaseUrl';

// 환경변수에서 API 기본 URL 가져오기
const API_BASE_URL = getApiBaseUrl();
const API_PREFIX = '/api'; // API 프록시 경로

// Axios 인스턴스 생성
const paymentApi = axios.create({
  baseURL: `${API_BASE_URL}${API_PREFIX}`,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 공통 인터셉터 설정
setupApiInterceptors(paymentApi);

// 승인일자 형식 변환 함수 (YYMMDDHHMMSS → ISO 형식)
const formatApprovalDate = (dateString: string): string => {
  if (!dateString || dateString.length !== 12) {
    console.warn('잘못된 승인일자 형식:', dateString);
    return new Date().toISOString(); // 기본값으로 현재 시간 반환
  }

  try {
    // YYMMDDHHMMSS 형식을 파싱
    const year = 2000 + parseInt(dateString.substring(0, 2)); // YY → YYYY
    const month = parseInt(dateString.substring(2, 4)) - 1; // MM (0-based)
    const day = parseInt(dateString.substring(4, 6)); // DD
    const hour = parseInt(dateString.substring(6, 8)); // HH
    const minute = parseInt(dateString.substring(8, 10)); // MM
    const second = parseInt(dateString.substring(10, 12)); // SS

    console.log('승인일자 변환:', {
      원본: dateString,
      파싱결과: { year, month: month + 1, day, hour, minute, second },
      '0-based월': month
    });

    const date = new Date(year, month, day, hour, minute, second);
    const isoString = date.toISOString();

    console.log('최종 변환 결과:', {
      원본: dateString,
      변환된날짜: isoString,
      '실제월': date.getMonth() + 1,
      '실제년월': `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
    });

    return isoString;
  } catch (error) {
    console.warn('승인일자 변환 실패:', dateString, error);
    return new Date().toISOString();
  }
};

// 테스트: 승인일자 변환 확인
console.log('=== 승인일자 변환 테스트 ===');
console.log('250213161336 변환 결과:', formatApprovalDate('250213161336'));
console.log('250315103000 변환 결과:', formatApprovalDate('250315103000'));

// 결제 관리 서비스 클래스
export class PaymentService {

  // 결제 목록 조회
  static async getPayments(params: PaymentSearchParams = {}): Promise<PaymentListResponse> {
    try {
      const {
        year_month,
        q,
        sort_by = 'create_datetime',
        order = 'desc',
        page = 1,
        limit = 15
      } = params;

      // 쿼리 파라미터 구성
      const queryParams = new URLSearchParams({
        sort_by,
        order,
        page: page.toString(),
        limit: limit.toString(),
      });

      // 연월 필터가 있는 경우 추가
      if (year_month && year_month.trim()) {
        queryParams.append('year_month', year_month.trim());
      }

      // 검색어가 있는 경우 추가
      if (q && q.trim()) {
        queryParams.append('q', q.trim());
      }

      console.log('🔄 결제 목록 API 호출 (단독):', {
        url: `/payments?${queryParams.toString()}`,
        params: { year_month, q, sort_by, order, page, limit }
      });

      const response = await paymentApi.get(`/payments?${queryParams.toString()}`);

      console.log('✅ 결제 목록 API 응답 성공:', response.data);

      // API 응답이 null이나 undefined인 경우 빈 응답 반환
      if (!response.data) {
        return {
          payments: [],
          total: 0,
          page: page,
          limit: limit,
          totalPages: 0
        };
      }

      // API 응답이 배열인 경우 변환
      if (Array.isArray(response.data)) {
        return {
          payments: response.data || [],
          total: response.data?.length || 0,
          page: page,
          limit: limit,
          totalPages: Math.ceil((response.data?.length || 0) / limit)
        };
      }

      // API 응답이 객체인 경우 안전하게 반환
      const rawItems = response.data.items || response.data.payments || [];

      // API 응답 필드명을 기존 모델에 맞게 변환
      const transformedPayments = rawItems.map((item: any) => ({
        승인날짜: formatApprovalDate(item.승인일자 || item.승인날짜 || ''),
        결제유저명: item.결제유저명 || '',
        결제상품명: item.결제상품명 || '',
        결제금액: item.결제금액 || 0,
        결제수단: item.결제수단 || '',
        카드사: item.카드사 || '',
        카드번호: item.카드번호 || ''
      }));

      return {
        payments: transformedPayments,
        total: response.data.total || 0,
        page: response.data.page || page,
        limit: response.data.limit || limit,
        totalPages: response.data.total_pages || response.data.totalPages || 0
      };
    } catch (error: any) {
      console.error('결제 목록 조회 실패:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
        url: error.config?.url
      });
      throw error;
    }
  }

  // 결제 통계와 목록을 함께 조회 (한 번의 API 호출)
  static async getPaymentStatsAndList(params: PaymentSearchParams = {}): Promise<{
    stats: PaymentStats;
    listResponse: PaymentListResponse;
  }> {
    try {
      const {
        year_month,
        q,
        sort_by = 'create_datetime',
        order = 'desc',
        page = 1,
        limit = 15
      } = params;

      // 쿼리 파라미터 구성
      const queryParams = new URLSearchParams({
        sort_by,
        order,
        page: page.toString(),
        limit: limit.toString(),
      });

      // 연월 필터가 있는 경우 추가
      if (year_month && year_month.trim()) {
        queryParams.append('year_month', year_month.trim());
      }

      // 검색어가 있는 경우 추가
      if (q && q.trim()) {
        queryParams.append('q', q.trim());
      }

      console.log('🔄 결제 통계+목록 통합 API 호출 (중복 방지):', {
        url: `/payments?${queryParams.toString()}`,
        params: { year_month, q, sort_by, order, page, limit }
      });

      const response = await paymentApi.get(`/payments?${queryParams.toString()}`);

      console.log('✅ 결제 통계+목록 통합 API 응답 성공:', response.data);

      // API 응답이 null이나 undefined인 경우 기본값 반환
      if (!response.data) {
        console.warn('⚠️ API 응답 데이터가 없습니다. 기본값을 반환합니다.');
        return {
          stats: {
            월결제금액: 0,
            월결제건수: 0,
            월결제고객수: 0,
            증감금액: 0,
            증감건수: 0,
            증감고객수: 0
          },
          listResponse: {
            payments: [],
            total: 0,
            page: page,
            limit: limit,
            totalPages: 0
          }
        };
      }

      console.log('🔍 API 응답 데이터 상세:', JSON.stringify(response.data, null, 2));

      // 통계 데이터 추출 (안전하게)
      let stats: PaymentStats = {
        월결제금액: 0,
        월결제건수: 0,
        월결제고객수: 0,
        증감금액: 0,
        증감건수: 0,
        증감고객수: 0
      };

      const summary = response.data.summary;
      if (summary && typeof summary === 'object') {
        const 기준월고객 = Number(summary.기준월_결제고객수) || 0;
        const 전월고객 = Number(summary.전월_결제고객수) || 0;
        stats = {
          월결제금액: Number(summary.기준월_결제금액) || 0,
          월결제건수: Number(summary.기준월_결제건수) || 0,
          월결제고객수: 기준월고객,
          증감금액: (Number(summary.기준월_결제금액) || 0) - (Number(summary.전월_결제금액) || 0),
          증감건수: (Number(summary.기준월_결제건수) || 0) - (Number(summary.전월_결제건수) || 0),
          증감고객수: 기준월고객 - 전월고객
        };
        console.log('📊 추출된 통계 데이터:', stats);
      } else {
        console.warn('⚠️ 통계 데이터(summary)가 없거나 형식이 올바르지 않습니다.');
      }

      // 목록 데이터 추출 및 변환 (안전하게)
      const rawItems = Array.isArray(response.data.items) ? response.data.items :
        Array.isArray(response.data.payments) ? response.data.payments : [];

      console.log('📦 원본 목록 데이터 (rawItems):', rawItems.length, '개');

      const transformedPayments = rawItems.map((item: any, index: number) => {
        // 각 아이템이 객체인지, 필요한 속성이 있는지 확인
        if (!item || typeof item !== 'object') {
          console.warn(`⚠️ 목록 아이템 ${index}가 객체가 아닙니다:`, item);
          return null; // 또는 기본 Payment 객체 반환
        }
        const paymentItem = {
          승인날짜: formatApprovalDate(String(item.승인일자 || item.승인날짜 || '')),
          결제유저명: String(item.결제유저명 || ''),
          결제상품명: String(item.결제상품명 || ''),
          결제금액: Number(item.결제금액) || 0,
          결제수단: String(item.결제수단 || ''),
          카드사: String(item.카드사 || ''),
          카드번호: String(item.카드번호 || '')
        };
        // console.log(`📄 변환된 아이템 ${index}:`, paymentItem);
        return paymentItem;
      }).filter(Boolean) as Payment[]; // null인 아이템 제거 및 타입 단언

      console.log('💳 변환된 결제 목록 (transformedPayments):', transformedPayments.length, '개');

      const listResponse: PaymentListResponse = {
        payments: transformedPayments,
        total: Number(response.data.total) || 0,
        page: Number(response.data.page) || page,
        limit: Number(response.data.limit) || limit,
        totalPages: Number(response.data.total_pages || response.data.totalPages) || 0
      };

      console.log('🚚 최종 반환될 listResponse:', listResponse);

      return { stats, listResponse };
    } catch (error: any) {
      console.error('결제 통계+목록 조회 실패:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
        url: error.config?.url
      });
      throw error;
    }
  }

  // 결제 통계만 조회 (기존 호환성 유지)
  static async getPaymentStats(yearMonth: string): Promise<PaymentStats> {
    try {
      const result = await this.getPaymentStatsAndList({ year_month: yearMonth });
      return result.stats;
    } catch (error: any) {
      console.error('결제 통계 조회 실패:', error);
      throw error;
    }
  }

  // 목업 결제 목록 데이터 (API 실패 시 사용)
  static getMockPayments(params: PaymentSearchParams = {}): PaymentListResponse {
    const { page = 1, limit = 15, q, year_month } = params;

    const mockData: Payment[] = [
      {
        승인날짜: formatApprovalDate("250213161336"),
        결제유저명: "브랜드인사이트",
        결제상품명: "POINT_CUSTOM",
        결제금액: 10000,
        결제수단: "CARD",
        카드사: "롯데",
        카드번호: "51379200****3891"
      },
      {
        승인날짜: formatApprovalDate("250115103000"),
        결제유저명: "brandtts",
        결제상품명: "Lite-625,000",
        결제금액: 550000,
        결제수단: "CARD",
        카드사: "신한",
        카드번호: "51379200****1234"
      },
      {
        승인날짜: formatApprovalDate("250110143000"),
        결제유저명: "testuser",
        결제상품명: "Basic-300,000",
        결제금액: 300000,
        결제수단: "CARD",
        카드사: "국민",
        카드번호: "51379200****5678"
      },
      {
        승인날짜: formatApprovalDate("250105093000"),
        결제유저명: "demouser",
        결제상품명: "Premium-1,000,000",
        결제금액: 1000000,
        결제수단: "CARD",
        카드사: "하나",
        카드번호: "51379200****9012"
      },
      {
        승인날짜: formatApprovalDate("241228163000"),
        결제유저명: "sampleuser",
        결제상품명: "POINT_CUSTOM",
        결제금액: 50000,
        결제수단: "CARD",
        카드사: "우리",
        카드번호: "51379200****3456"
      },
      {
        승인날짜: formatApprovalDate("241220113000"),
        결제유저명: "exampleuser",
        결제상품명: "Standard-500,000",
        결제금액: 500000,
        결제수단: "CARD",
        카드사: "농협",
        카드번호: "51379200****7890"
      }
    ];

    // 검색 필터링
    let filteredData = mockData;
    if (q && q.trim()) {
      const searchTerm = q.toLowerCase();
      filteredData = mockData.filter(payment =>
        payment.결제유저명.toLowerCase().includes(searchTerm) ||
        payment.결제상품명.toLowerCase().includes(searchTerm) ||
        payment.카드사.toLowerCase().includes(searchTerm)
      );
    }

    // 연월 필터링
    if (year_month && year_month.trim()) {
      const filterYearMonth = year_month.trim();
      console.log('목업 데이터 연월 필터링:', {
        필터조건: filterYearMonth,
        전체데이터수: filteredData.length
      });

      filteredData = filteredData.filter(payment => {
        const paymentDate = new Date(payment.승인날짜);
        const paymentYearMonth = `${paymentDate.getFullYear()}-${String(paymentDate.getMonth() + 1).padStart(2, '0')}`;

        console.log('결제 데이터 필터링 체크:', {
          결제승인날짜: payment.승인날짜,
          파싱된날짜: paymentDate,
          추출된년월: paymentYearMonth,
          필터조건: filterYearMonth,
          매칭여부: paymentYearMonth === filterYearMonth
        });

        return paymentYearMonth === filterYearMonth;
      });

      console.log('필터링 후 데이터수:', filteredData.length);
    }

    // 페이지네이션
    const startIndex = (page - 1) * limit;
    const endIndex = startIndex + limit;
    const paginatedData = filteredData.slice(startIndex, endIndex);

    return {
      payments: paginatedData,
      total: filteredData.length,
      page: page,
      limit: limit,
      totalPages: Math.ceil(filteredData.length / limit)
    };
  }

  // 목업 결제 통계 데이터 (API 실패 시 사용)
  static getMockPaymentStats(yearMonth: string): PaymentStats {
    return {
      월결제금액: 500000000,
      월결제건수: 5600,
      월결제고객수: 1200,
      증감금액: 40000000,
      증감건수: -12,
      증감고객수: 45
    };
  }
} 